"use client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { fillTemplate } from "../../guidePage.util";
import { useCopyFeedback, useStoredValue, writeStoredValue } from "../../hooks";

export interface GuideChecklistCopy {
  // "{done} of {total}".
  countTemplate: string;
  copyLabel: string;
  copiedLabel: string;
  resetLabel: string;
  fine: string;
}

interface GuideChecklistProps {
  // Storage id ("g_plan"), shared with the preview's key shape.
  progressId: string;
  title: string;
  items: string[];
  copy: GuideChecklistCopy;
}

const parseTicks = (raw: string | null): number[] => {
  try {
    const parsed: unknown = JSON.parse(raw ?? "[]");
    return Array.isArray(parsed)
      ? parsed.filter((item): item is number => typeof item === "number")
      : [];
  } catch {
    return [];
  }
};

// The guide's tick-off checklist, saved in this browser only, with a
// progress bar, "Copy checklist" (as [x] / [ ] text) and "Clear ticks".
const GuideChecklist = ({ progressId, title, items, copy }: GuideChecklistProps) => {
  const key = `fydo.cl.${progressId}`;
  const ticks = parseTicks(useStoredValue(key));
  const { isCopied, copy: copyText } = useCopyFeedback();

  const toggle = (index: number) =>
    writeStoredValue(
      key,
      JSON.stringify(
        ticks.includes(index)
          ? ticks.filter((item) => item !== index)
          : [...ticks, index].sort((a, b) => a - b),
      ),
    );

  const asText = `${title}\n${items
    .map((item, index) => `${ticks.includes(index) ? "[x]" : "[ ]"} ${item}`)
    .join("\n")}`;
  const countText = fillTemplate(copy.countTemplate, {
    done: ticks.length,
    total: items.length,
  });

  return (
    <div className="grid max-w-[820px] gap-3.5 rounded-[30px] bg-white p-5 text-ink shadow-card sm:p-7">
      <div className="grid grid-cols-[1fr_auto] items-center gap-3.5 sm:grid-cols-[auto_1fr_auto]">
        <h3 className="text-lg font-bold">{title}</h3>
        <Progress
          value={(ticks.length / items.length) * 100}
          aria-label={countText}
          className="order-3 col-span-full h-2.5 bg-line sm:order-none sm:col-span-1"
          indicatorClassName="bg-tone-mint-ink motion-reduce:transition-none"
        />
        <span aria-live="polite" className="text-sm font-bold text-ink-muted">
          {countText}
        </span>
      </div>
      <ul className="grid gap-1">
        {items.map((item, index) => {
          const isDone = ticks.includes(index);
          return (
            <li
              key={item}
              className={cn(
                "rounded-[14px] transition-colors motion-reduce:transition-none",
                isDone && "bg-tone-mint/40",
              )}
            >
              <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 text-base leading-snug">
                <input
                  type="checkbox"
                  checked={isDone}
                  onChange={() => toggle(index)}
                  className="mt-0.5 size-5 shrink-0 accent-tone-mint-ink"
                />
                <span
                  className={cn(
                    isDone &&
                      "text-ink-muted line-through decoration-tone-mint-ink/50",
                  )}
                >
                  {item}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          type="button"
          variant="brandOutline"
          size="pillSm"
          onClick={() => copyText(asText)}
        >
          {isCopied ? copy.copiedLabel : copy.copyLabel}
        </Button>
        <button
          type="button"
          onClick={() => writeStoredValue(key, "[]")}
          className="cursor-pointer px-2.5 py-2 font-bold text-primary hover:underline"
        >
          {copy.resetLabel}
        </button>
      </div>
      <p className="text-sm text-ink-muted">{copy.fine}</p>
    </div>
  );
};

export default GuideChecklist;
